/* eslint-disable tailwindcss/no-arbitrary-value */
'use client';

import classNames from 'classnames';
import { useMemo } from 'react';
import { PremiumProduct } from '../../../../../types/car-insurance';
import { CmsProduct } from '../../../../../types/cmsProduct';
import { CarInsurance } from '../../../../../types/cmsTypes';
import { useCmsClientContext } from '../../../../../utils/cmsPageContext';
import { formatNumberWithSeparator } from '../../../../../utils/helpers';
import { getClientTranslations } from '../../../../../utils/translations/getClientTranslations';
import { useSafeSearchParams } from '../../../../../utils/useSafeSearchParams';
import { CmsImage } from '../../../../CmsImage';
import { CarInsuranceQuote } from '../../../../ProductCard/SectionPremiumCI';
import { getPrice } from '../../../../ProductCard/utils';
import { useSafeCarInsuranceContext } from '../../../context/useSafeCarInsurance';
import { EXCLUSIVE_REWARDS_TRANSLATIONS } from '../../i18n';
import {
  AgeIcon,
  BrandIcon,
  CarIcon,
  CategoryIcon,
  DateIcon,
  DrivingIcon,
  GenderIcon,
  ListIcon,
  MaritalStatusIcon,
} from '../../icons';

const AttributeRow: React.FC<{
  name: string;
  value: string | number;
  icon: React.ReactNode;
  isStartAlignment?: boolean;
}> = ({ name, value, icon, isStartAlignment }) => {
  const alignment = isStartAlignment ? 'items-start' : 'items-center';
  return (
    <div className={`flex ${alignment} gap-2`}>
      <span
        className={`flex ${alignment} gap-2 md:w-37 md:min-w-37 w-39 min-w-39`}
      >
        <span>{icon}</span>
        <span className="alpha-badge text-text/secondary-lighter">{name}</span>
      </span>
      <span className="alpha-badge text-text/primary truncate max-w-fit">
        {value}
      </span>
    </div>
  );
};

interface BrandUserInfoProps {
  product: CmsProduct | null;
  carInsuranceQuotes: CarInsuranceQuote[];
  productWithLivePrice: PremiumProduct[];
  carInsuranceSection: CarInsurance | undefined;
}

const BrandUserInfo: React.FC<BrandUserInfoProps> = ({
  product,
  carInsuranceQuotes,
  productWithLivePrice,
  carInsuranceSection,
}) => {
  const context = useCmsClientContext();
  const { brand, lang } = context;
  const staticText = getClientTranslations(
    EXCLUSIVE_REWARDS_TRANSLATIONS,
    context,
  );
  const carInsuranceContext = useSafeCarInsuranceContext();

  // Get vehicle and driver data from context
  const vehicleData = carInsuranceContext?.requestParams?.vehicle?.value;
  const driverData = carInsuranceContext?.requestParams?.driver?.value;
  const ncd = carInsuranceContext?.requestParams?.ncd?.value;
  const useBolttechData = carInsuranceContext?.useBolttechData;

  const makeName = useMemo(() => {
    const vehicleMakers = carInsuranceContext?.data?.vehicleMakers?.value ?? [];
    if (!vehicleData) return '';
    if (useBolttechData) {
      return (
        vehicleMakers.find((maker) => maker.value === vehicleData.makeName)
          ?.label || ''
      );
    }
    return vehicleData.makeName;
  }, [
    useBolttechData,
    vehicleData,
    carInsuranceContext?.data?.vehicleMakers?.value,
  ]);

  const modelName = useMemo(() => {
    const vehicleModels = carInsuranceContext?.data?.vehicleModels?.value ?? [];
    if (!vehicleData) return '';
    if (useBolttechData) {
      return (
        vehicleModels.find((model) => model.value === vehicleData.modelCode)
          ?.label || ''
      );
    }
    return vehicleData.modelName;
  }, [
    useBolttechData,
    vehicleData,
    carInsuranceContext?.data?.vehicleModels?.value,
  ]);

  const trimName = useMemo(() => {
    const vehicleTrims = carInsuranceContext?.data?.vehicleTrims?.value ?? [];
    if (!vehicleData) return '';
    if (useBolttechData) {
      return (
        vehicleTrims.find((trim) => trim.value === vehicleData.trim)?.label ||
        ''
      );
    }
    return vehicleData.trim;
  }, [
    useBolttechData,
    vehicleData,
    carInsuranceContext?.data?.vehicleTrims?.value,
  ]);

  const totalDrivingExperienceYears = Number(
    driverData?.totalDrivingExperience ?? 0,
  );
  const totalDrivingExperienceYearsText =
    totalDrivingExperienceYears >= 3
      ? staticText.drivingExperience3PlusYears
      : totalDrivingExperienceYears === 2
        ? staticText.drivingExperience2Years
        : totalDrivingExperienceYears === 1
          ? staticText.drivingExperience1Year
          : staticText.drivingExperience0Years;

  const priceDirection = lang === 'zh-hk' ? 'flex-col-reverse' : 'flex-col';

  const params = useSafeSearchParams();

  const hideVehicleDetails =
    params != null &&
    params.has('showVehicle') &&
    params?.getAll('showVehicle')[0] === 'false';
  const carInsuranceQuote = carInsuranceQuotes?.find(
    (quote) => quote.productId === product?.title,
  );
  const premium = productWithLivePrice?.find(
    (prod) => prod.brokerProductCode === product?.title?.slice(3),
  );
  const viewPrice = formatNumberWithSeparator({
    value:
      premium?.discountValue ??
      premium?.duePremium ??
      (carInsuranceQuote && getPrice(carInsuranceQuote)) ??
      carInsuranceSection?.priceStatic ??
      0,
    thousandsSeparator: ',',
    alwaysTwoDecimals: true,
  });

  if (!product) {
    return null;
  }

  return (
    <div className="w-full md:w-72 lg:w-[346px] shrink-0 self-start flex flex-col justify-start items-start rounded-2xl bg-white shadow-md">
      <div className="w-full p-4 md:p-6">
        <div className="flex flex-col justify-center gap-4">
          <div className="flex flex-row justify-center rounded-lg outline outline-1 outline-gray-300 p-4 md:h-22 sm:h-18 bg-white">
            {product.product_image && (
              <CmsImage
                className="size-full object-contain"
                sizes={{ _: 266 }}
                src={product.product_image}
                alt={product.product_name ?? 'product image'}
                width={266}
                height={56}
              />
            )}
          </div>
          <div className="max-w-full self-start text-left">
            <p className="alpha-title-2 text-text/primary break-normal">
              {product.product_name}
            </p>
          </div>
          <div className="flex flex-col">
            {!!viewPrice && viewPrice !== '0.00' && (
              <div className={classNames('flex', priceDirection)}>
                <span className="text-text/secondary-lighter alpha-badge">
                  {staticText.startingAt}
                </span>
                <span className="text-text/primary alpha-title-2">
                  {carInsuranceSection?.prefix}
                  {viewPrice}
                  {carInsuranceSection?.suffix}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
      {!hideVehicleDetails ? (
        <div className="border-t p-4 md:p-6 border-gray-300 w-full gap-2">
          <div className="flex flex-col gap-2">
            <AttributeRow
              name={staticText.vehicleMake}
              value={makeName}
              icon={<BrandIcon />}
            />
            <AttributeRow
              name={staticText.vehicleModel}
              value={modelName}
              icon={<CarIcon />}
            />
            {brand === 'singsaver' && useBolttechData && trimName && (
              <AttributeRow
                name={staticText.variant}
                value={trimName}
                icon={<CategoryIcon />}
              />
            )}
            <AttributeRow
              name={staticText.manufactureYear}
              value={vehicleData?.manufactureYear ?? ''}
              icon={<DateIcon />}
            />
            <div className="flex flex-col gap-2 mt-5">
              <AttributeRow
                name={staticText.gender}
                value={
                  driverData?.gender === 'M'
                    ? staticText.genderMale
                    : staticText.genderFemale
                }
                icon={<GenderIcon />}
              />
              <AttributeRow
                name={staticText.maritalStatus}
                value={
                  driverData?.maritalStatus === 'S'
                    ? staticText.maritalStatusSingle
                    : staticText.maritalStatusMarried
                }
                icon={<MaritalStatusIcon />}
              />
              <AttributeRow
                name={staticText.age}
                value={`${driverData?.age} ${staticText.ageYears}`}
                icon={<AgeIcon />}
              />
              <AttributeRow
                name={staticText.drivingExperience}
                value={totalDrivingExperienceYearsText}
                icon={<DrivingIcon />}
              />
              <AttributeRow
                name={staticText.ncd}
                value={`${ncd}%`}
                icon={<BrandIcon />}
              />
              <AttributeRow
                name={staticText.numberOfClaims}
                value={driverData?.numberOfClaims ?? 0}
                icon={<ListIcon />}
                isStartAlignment
              />
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default BrandUserInfo;
